import { useEffect, useState } from 'react'
import { api, type Ausstieg } from '../net/apiClient.js'

/**
 * Das Band, das nicht weggeklickt werden kann.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Warum es keinen Schliessen-Knopf hat
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Weil der Ablauf niemals überraschend passieren darf. Ein Hinweis, den man
 * wegklickt, ist am dritten Tag weg und am siebten nie gesehen worden – und
 * dann steht ein Betrieb vor einem gesperrten Zugang und hat nie erfahren,
 * dass es so weit kommt. Das Band bleibt also stehen, solange der Grund
 * besteht, und es verschwindet, sobald er weg ist.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Und warum es bei einem normalen Kunden gar nicht erscheint
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Ein Warnband, das immer da ist, ist Tapete. Der Server liefert
 * `ausstieg: null` für jeden Betrieb, der in keinem Fenster steht; dann wird
 * hier nichts gezeichnet. Entschieden wird das NICHT in der Oberfläche: Die
 * Tage kommen aus `vertragsautomatik.ausstiegsfenster()`, derselben Stelle,
 * die auch sperrt.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Was hier NICHT stehen darf
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Ein Löschdatum. Solange der sichere Purge fehlt, gibt der Server
 * `loeschungVorgesehenAm: null` heraus – und diese Datei zeigt das Feld
 * ausschliesslich, wenn es gefüllt ist. Kein Ersatztext, keine Andeutung: Ein
 * Datum auf dem Bildschirm ist eine Zusage.
 */

/**
 * Was dasteht – nach PHASE und nicht nach Zustand.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Die vier Sätze, und warum sie sich nicht vermischen dürfen
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Der Ablauf kennt vier Abschnitte, und jeder hat genau EINE Frist, auf die es
 * ankommt. Das Band nennt diese eine – nicht zwei, und nie eine Zahl, die zu
 * einem anderen Datum gehört:
 *
 *   laeuft      Die Testphase oder die bezahlte Periode läuft. Die Frist ist
 *               Tag 0, und wenn ein Zahlungsmittel hinterlegt ist, ist Tag 0
 *               ein BUCHUNGSTERMIN und kein Ablauf. Genau das sagt der Satz
 *               dann auch.
 *   nachfrist   Die Zahlung ist offen. Die Frist ist der Tag, an dem der
 *               Lesemodus beginnt.
 *   lesemodus   Lesen und exportieren ja, neue Vorgänge nein. Die Frist ist
 *               der vorgesehene Löschtag.
 *   geloescht   Die Frist ist abgelaufen.
 *
 * Gerechnet wird hier nichts. Phase, Daten und Resttage kommen aus
 * `vertragsautomatik.ausstiegsfenster()` – derselben Stelle, die auch sperrt.
 */
function wortlaut(a: Ausstieg): { titel: string; satz: string; knopf: string } {
  const bis = (iso: string) => datum(iso)
  const knopf = a.grund === 'zahlung_offen' ? 'Zahlung aktualisieren' : 'Abo aktivieren'
  const was =
    a.grund === 'test_abgelaufen'
      ? 'Die Testphase'
      : a.grund === 'abo_beendet'
        ? 'Dein Abo'
        : 'Dein Zahlungszeitraum'

  /* ── Der Löschsatz, einmal formuliert ───────────────────────────────── */
  const loeschung =
    a.loeschungVorgesehenAm !== null
      ? ` Danach ist der Betrieb zur Löschung vorgesehen – am ${bis(a.loeschungVorgesehenAm)}.`
      : ''

  switch (a.phase) {
    case 'laeuft':
      /*
        Ein Buchungstermin ist keine Drohung.

        Steht ein Zahlungsmittel bereit, endet die Frist nicht, sondern geht
        über: Am selben Tag wird abgebucht, und das gehört vorher gesagt –
        der Gründerentscheid nennt es «rechtzeitige Ankündigung mit
        tatsächlichem Buchungsdatum».
      */
      /*
        Gekündigt ist nicht dasselbe wie «ohne Zahlungsmittel».

        Beide Lagen haben keinen Buchungstermin, und bis zum 27. Oktober 2026
        bekamen sie denselben Satz: «Hinterlege dein Zahlungsmittel, wenn du
        weiterarbeiten willst.» Wer gerade gekündigt hat, wird damit zu etwas
        aufgefordert, das er ausdrücklich nicht will – und der Satz steht
        neben «Gekündigt» und «Kündigung zurücknehmen».

        Nach einer Kündigung ist nichts zu tun, und genau das gehört da hin.
      */
      if (a.verlaengertNicht) {
        return {
          titel: a.grund === 'test_abgelaufen' ? 'Deine Testphase endet' : `${was} endet`,
          satz:
            `${was} endet am ${bis(a.endeAm)}, weil du gekündigt hast. Bis dahin ` +
            'arbeitest du normal weiter; abgebucht wird nichts mehr.',
          knopf
        }
      }
      return a.abbuchungAm !== null
        ? {
            titel:
              a.grund === 'test_abgelaufen' ? 'Deine Testphase endet' : 'Nächste Abbuchung',
            satz: `${was} endet am ${bis(a.endeAm)}. An diesem Tag buchen wir ab; bis dahin entstehen keine Kosten.`,
            knopf
          }
        : {
            titel: a.grund === 'test_abgelaufen' ? 'Deine Testphase endet' : `${was} endet`,
            satz: `${was} endet am ${bis(a.endeAm)}. Hinterlege dein Zahlungsmittel, wenn du weiterarbeiten willst.`,
            knopf
          }

    case 'nachfrist':
      /*
        Die Nachfrist ist der Abschnitt, in dem sich noch nichts geändert hat –
        und genau das steht da. Ein Satz, der hier schon vom Lesemodus spricht,
        verschiebt die Dringlichkeit um eine Woche.
      */
      return {
        titel:
          a.grund === 'zahlung_offen'
            ? 'Zahlung konnte nicht verarbeitet werden'
            : `${was} ist abgelaufen`,
        satz:
          a.sperreAm === null
            ? 'Du arbeitest unverändert weiter. Bitte bring die Zahlung in Ordnung.'
            : `Du arbeitest bis zum ${bis(a.sperreAm)} unverändert weiter. Danach wechselt der Zugang in den Lesemodus: ansehen und exportieren ja, neue Rechnungen und Rapporte nein.`,
        knopf
      }

    case 'lesemodus':
      return {
        titel: 'Lesemodus – neue Einträge sind gesperrt',
        satz:
          `Du siehst alle Daten und kannst sie exportieren; neue Rechnungen, Offerten und ` +
          `Rapporte entstehen keine.${loeschung} Sobald die Zahlung durch ist, geht es sofort weiter.`,
        knopf
      }

    case 'geloescht':
      return {
        titel: 'Die Frist ist abgelaufen',
        satz:
          `Dieser Betrieb ist zur Löschung vorgesehen` +
          `${a.loeschungVorgesehenAm !== null ? ` – seit dem ${bis(a.loeschungVorgesehenAm)}` : ''}. ` +
          `Melde dich bei Raporto, wenn du deine Daten noch brauchst.`,
        knopf
      }
  }
}

/** «2027-03-17» als «17.03.2027» – ohne Zeitzonenrechnung auf einem reinen Tag. */
function datum(iso: string): string {
  const [j, m, t] = iso.split('-')
  return t && m && j ? `${t}.${m}.${j}` : iso
}

export function Ausstiegsband({
  aufAbo
}: {
  /** Zum Abobereich – dort stehen Zahlung, Paket und Kündigung. */
  aufAbo: () => void
}): JSX.Element | null {
  const [ausstieg, setAusstieg] = useState<Ausstieg | null>(null)

  useEffect(() => {
    let abgebrochen = false
    void (async () => {
      try {
        const stand = await api.abo()
        if (!abgebrochen) setAusstieg(stand.ausstieg)
      } catch {
        /*
          Kein Band, keine Meldung.

          Diese Abfrage ist dem Betrieb nicht aufgefallen, und ein Fehler darin
          soll es auch nicht. Wer keinen Zugriff auf den Abobereich hat – ein
          Monteur etwa –, bekommt hier 403 und einfach kein Band; das ist
          richtig, denn er könnte ohnehin nichts davon tun.
        */
      }
    })()
    return () => {
      abgebrochen = true
    }
  }, [])

  if (ausstieg === null) return null

  const w = wortlaut(ausstieg)
  const dringend = ausstieg.lage !== 'laeuft'

  return (
    <div
      className={dringend ? 'ausstiegsband dringend' : 'ausstiegsband'}
      id="ausstiegsband"
      role="status"
      data-grund={ausstieg.grund}
      data-lage={ausstieg.lage}
    >
      <div className="ausstiegsband-text">
        <strong>{w.titel}</strong>
        {/*
          Die Resttage – und zwar zu DEM Datum, das im Satz daneben steht.

          Hier stand `tageBisSperre`, immer. Darüber im Satz stand aber das
          Ende der Testphase, und das ist ein anderer Tag: Die Sperre kommt
          erst nach der Lesefrist, in der ein Betrieb seine Daten noch
          exportieren kann. Zusammen ergab das eine Zeile wie «Deine
          Testphase endet am 09.11. … Noch 36 Tage» – zwei Zahlen, die sich
          widersprechen, und keine Stelle, an der stünde, welche gilt.

          Jetzt zählt die Zeile GENAU EINE Frist, und zwar die der laufenden
          Phase: bis zum Buchungstermin, solange die Frist läuft; bis zum
          Lesemodus während der Nachfrist; bis zum vorgesehenen Löschtag im
          Lesemodus. Welche das ist, sagt die Marke dazu – «noch 5 Tage» allein
          war die halbe Auskunft.

          Und sie steht neben der Überschrift, nicht hinter dem Satz. Das ist
          eine Messung und kein Geschmack: Auf 400 Pixeln Breite passen
          Überschrift und Marke zusammen in eine Zeile, der Satz braucht die
          nächste – stand die Marke dahinter, ergab sie eine eigene vierte
          Zeile. Das Band war so 181 Pixel hoch, ein Viertel eines Telefons.
        */}
        {(() => {
          const frist: { tage: number | null; wozu: string } =
            ausstieg.phase === 'laeuft'
              ? {
                  tage: ausstieg.tageBisEnde,
                  wozu: ausstieg.abbuchungAm !== null ? ' bis zur Abbuchung' : ''
                }
              : ausstieg.phase === 'nachfrist'
                ? { tage: ausstieg.tageBisSperre, wozu: ' Nachfrist' }
                : ausstieg.phase === 'lesemodus'
                  ? { tage: ausstieg.tageBisLoeschung, wozu: ' zum Exportieren' }
                  : { tage: null, wozu: '' }
          if (frist.tage === null) return null
          const offen = Math.max(frist.tage, 0)
          return (
            <span className="ausstiegsband-tage" id="ausstiegsband-tage">
              {offen === 1 ? 'noch 1 Tag' : `noch ${offen} Tage`}
              {frist.wozu}
            </span>
          )
        })()}
        <span>{w.satz}</span>
      </div>
      <div className="ausstiegsband-knoepfe">
        <button type="button" className="o-btn o-btn-haupt" id="band-abo" onClick={aufAbo}>
          {w.knopf}
        </button>
        {/*
          Der Export ist ein Download und kein Bildschirm – deshalb ein Link
          auf dieselbe Adresse, die «Was ist offen» schon benutzt, und keine
          zweite Ansicht daneben. Nach der Sperre steht er nicht mehr da: Die
          Anmeldung kommt dann gar nicht mehr zustande.
        */}
        {/*
          Der Export steht gerade im Lesemodus da – dort ist er die eine
          Handlung, die noch Daten rettet. Weg ist er erst, wenn die Frist
          abgelaufen ist.
        */}
        {ausstieg.phase !== 'geloescht' && (
          <a className="o-btn" id="band-export" href="/exports/zip">
            Daten exportieren
          </a>
        )}
      </div>
    </div>
  )
}
